import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Check, ArrowLeft, Zap, Shield, Mail, Plus, Minus } from "lucide-react";
import api from "../lib/api";
import { addToCart } from "../lib/cart";
import { toast } from "sonner";
import ProductBadge from "../components/ProductBadge";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setProduct(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20 text-[#A1A1AA] font-mono">loading...</div>;
  if (!product) return (
    <div className="max-w-7xl mx-auto px-6 py-20 text-center">
      <div className="font-mono text-[#A1A1AA]">product not found</div>
      <Link to="/products" className="btn-ghost mt-4 inline-flex">Back to catalog</Link>
    </div>
  );

  const finalPrice = product.price - (product.discount || 0);
  const inStock = product.keys_available > 0 || product.stock_type === "unlimited";

  const handleAdd = () => {
    addToCart(product, qty);
    toast.success(`Added ${qty}× ${product.name} to cart`);
  };
  const handleBuy = () => {
    addToCart(product, qty);
    nav("/checkout");
  };

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-6 py-12">
      <Link to="/products" className="font-mono text-xs text-[#A1A1AA] hover:text-[#00FF66] inline-flex items-center gap-1 mb-8">
        <ArrowLeft className="w-3.5 h-3.5" /> back to catalog
      </Link>

      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <div className="surface aspect-[4/3] overflow-hidden bg-[#0a0a0f] relative">
            {product.images?.[activeImg] ? (
              <img src={product.images[activeImg]} alt={product.name} className="w-full h-full object-cover" />
            ) : product.logo_text ? (
              <ProductBadge text={product.logo_text} subText={product.logo_text_sub} color={product.logo_color || "#00FF66"} size="lg" />
            ) : (
              <div className="w-full h-full bg-grid" />
            )}
            <div className="absolute top-4 left-4 chip-mono">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FF66]" />{product.category}
            </div>
          </div>
          {product.images?.length > 1 && (
            <div className="mt-3 flex gap-2">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`surface aspect-square w-20 overflow-hidden ${activeImg === i ? "border-[#00FF66]" : ""}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#00FF66]">// {product.category}</div>
          <h1 data-testid="product-name" className="font-heading font-bold text-3xl md:text-4xl mt-2">{product.name}</h1>
          <p className="text-[#A1A1AA] mt-3 leading-relaxed">{product.description}</p>

          <div className="flex items-baseline gap-3 mt-6">
            <span data-testid="product-price" className="font-mono font-bold text-4xl text-[#00FF66]">${finalPrice.toFixed(2)}</span>
            {product.subscription_period && (
              <span className="font-mono text-sm text-[#A1A1AA] uppercase tracking-wider">/{product.subscription_period}</span>
            )}
            {product.discount > 0 && (
              <span className="font-mono text-sm text-[#71717A] line-through">${product.price.toFixed(2)}</span>
            )}
          </div>

          {product.requires_account_email && (
            <div className="mt-5 p-4 rounded-md border border-[#1F8FE8]/40 bg-[#1F8FE8]/10">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#5C9CD8] mb-1">// activation notice</div>
              <p className="text-sm text-white leading-relaxed">
                This is a <strong>{product.subscription_period || "1 year"} subscription</strong>. Activation happens on <strong>your own Autodesk account email</strong> — no key, no patch, no crack. You'll provide that email at checkout. Login to Autodesk normally with your own credentials after activation.
              </p>
              {product.platforms?.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA]">available on:</span>
                  {product.platforms.map((p) => (
                    <span key={p} data-testid={`platform-chip-${p.toLowerCase()}`} className="chip-mono !bg-[#1F8FE8]/10 !text-[#5C9CD8] !border-[#1F8FE8]/30">{p}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className={`mt-3 font-mono text-xs flex items-center gap-2 ${inStock ? "text-[#00FF66]" : "text-[#FF3B30]"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${inStock ? "bg-[#00FF66] animate-pulse" : "bg-[#FF3B30]"}`} />
            {inStock ? `in stock — ${product.stock_type === "unlimited" ? "unlimited" : product.keys_available + " keys"} ready` : "out of stock"}
          </div>

          {product.features?.length > 0 && (
            <ul className="mt-6 space-y-2">
              {product.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-[#00FF66] mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center surface">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 hover:text-[#00FF66]" data-testid="qty-decrement">
                <Minus className="w-3.5 h-3.5" />
              </button>
              <input value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="w-12 bg-transparent text-center font-mono outline-none" data-testid="qty-input" />
              <button onClick={() => setQty(qty + 1)} className="px-3 py-2 hover:text-[#00FF66]" data-testid="qty-increment">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
            <button data-testid="add-to-cart-btn" onClick={handleAdd} disabled={!inStock} className="btn-ghost flex-1">add to cart</button>
            <button data-testid="buy-now-btn" onClick={handleBuy} disabled={!inStock} className="btn-primary flex-1 justify-center">buy now</button>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-3">
            {[
              { i: Zap, t: "Instant delivery" },
              { i: Shield, t: "Genuine retail" },
              { i: Mail, t: "Email + on-site" },
            ].map((b, i) => (
              <div key={i} className="surface p-4">
                <b.i className="w-4 h-4 text-[#00FF66]" />
                <div className="font-mono text-[11px] text-[#A1A1AA] mt-2">{b.t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
